import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { useFormik } from 'formik';
import { deburr, get } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { createValidationSchema } from 'src/schemas/editProductDetails.schema';
import { round } from 'src/utils/number';

export interface ProductData {
  title: string;
  price: string;
  description: string;
  imageUrl: string;
  productDetails: Record<string, string>;
  manufacturingAndPackaging: Record<string, string>;
}

export interface FormData {
  itemName: string;
  price: string;
  mfgName: string;
  manufacturerNumber: string;
  brandName: string;
  uom: string;
  casePackUnits: string;
  weight: string;
  upc: string;
  unspsc: string;
}

interface UseEditItemInformationFormProps {
  productData: ProductData;
  isOpen: boolean;
}

interface UseEditItemInformationFormResult {
  formik: ReturnType<typeof useFormik<FormData>>;
  uomOptions: Array<{ text: string; value: string }>;
  selectedUOM: { text: string; value: string };
  uomFilterValue: string;
  handleUOMChange: (event: DropDownListChangeEvent) => void;
  handleUOMFilterChange: (event: any) => void;
  handleUOMBlur: (event: any) => void;
  getSaveButtonDisabledReason: (isSaving: boolean) => string | null;
}

const UOM_OPTIONS = ['CS', 'EA', 'BX', 'PK', 'CA', 'GL', 'LB', 'OZ', 'PT', 'QT', 'GA'];

const useEditItemInformationForm = ({
  productData,
  isOpen,
}: UseEditItemInformationFormProps): UseEditItemInformationFormResult => {
  const initialValues: FormData = useMemo(
    () => ({
      itemName: deburr(get(productData, 'title', '')).trim(),
      price: productData.price?.replace(/[^0-9.]/g, '') || '',
      mfgName: productData.manufacturingAndPackaging['Mfg Name'] || '',
      manufacturerNumber: productData.manufacturingAndPackaging['Mfg #'] || '',
      brandName: productData.manufacturingAndPackaging['Brand Name'] || '',
      uom: productData.manufacturingAndPackaging.UOM || '',
      casePackUnits: productData.manufacturingAndPackaging['Case Pack Units'] || '',
      weight: productData.manufacturingAndPackaging.Weight || '',
      upc: productData.productDetails.UPC || '',
      unspsc: productData.productDetails.UNSPSC || '',
    }),
    [productData]
  );

  const validationSchema = useMemo(
    () => createValidationSchema(initialValues.price, initialValues.uom),
    [initialValues.price, initialValues.uom]
  );

  const formik = useFormik<FormData>({
    initialValues,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: () => {
      // Handled in parent component
    },
  });

  const { resetForm } = formik;

  // State to track custom UOMs typed during the session
  const [customUOMs, setCustomUOMs] = useState<string[]>([]);
  // State to track filter input value for clearing placeholder
  const [uomFilterValue, setUomFilterValue] = useState<string>('');

  // Reset form and initialize custom UOM when dialog opens
  useEffect(() => {
    if (isOpen) {
      resetForm({ values: initialValues });
      setUomFilterValue('');

      // If initial UOM from database is custom (not in predefined list), preserve it
      const initialUom = initialValues.uom?.trim();
      if (initialUom && !UOM_OPTIONS.includes(initialUom)) {
        setCustomUOMs([initialUom]);
      } else {
        setCustomUOMs([]);
      }
    }
  }, [isOpen, initialValues, resetForm]);

  // Base options: predefined UOMs + any saved custom UOM (selected previously)
  const baseUomOptions = useMemo(() => {
    const existingOptions = UOM_OPTIONS.map((uom) => ({ text: uom, value: uom }));

    // Add saved custom UOM if exists (this persists after selection)
    const customUOMOptions =
      customUOMs.length > 0 && !UOM_OPTIONS.includes(customUOMs[0])
        ? [{ text: customUOMs[0], value: customUOMs[0] }]
        : [];

    return [...customUOMOptions, ...existingOptions];
  }, [customUOMs]);

  // Display options: filtered based on search input
  // Logic: Filter first → if no match found → show typed value as custom option
  const uomOptions = useMemo(() => {
    const filterValue = uomFilterValue.trim().toLowerCase();

    // If no filter, return base options
    if (!filterValue) {
      return baseUomOptions;
    }

    // Step 1: Filter existing options that contain the filter text
    const filteredOptions = baseUomOptions.filter((opt) =>
      opt.text.toLowerCase().includes(filterValue)
    );

    // Step 2: Check if filter exactly matches any predefined option (case-insensitive)
    const exactMatchExists = baseUomOptions.some((opt) => opt.text.toLowerCase() === filterValue);

    // Step 3: If filtered results exist, just show them (no custom option needed)
    if (filteredOptions.length > 0) {
      return filteredOptions;
    }

    // Step 4: No matches found - show typed value as custom option at top
    // along with all base options below for user to choose from
    if (!exactMatchExists && uomFilterValue.trim()) {
      const customOption = { text: uomFilterValue.trim(), value: uomFilterValue.trim() };
      return [customOption, ...baseUomOptions];
    }

    return baseUomOptions;
  }, [baseUomOptions, uomFilterValue]);

  const handleUOMChange = (event: DropDownListChangeEvent) => {
    const value = event.value?.text || event.value?.value || event.value || '';

    // Handle price decimal places based on UOM selection (lb = 3 decimals, others = 2)
    const isLb = value.toLowerCase() === 'lb';
    if (isLb) {
      formik.setFieldValue('price', round(Number(formik.values.price || '0'), 3), true);
    } else {
      formik.setFieldValue('price', round(Number(formik.values.price || '0'), 2), true);
    }
    formik.setFieldTouched('price', true, true);

    // Clear the filter input when a UOM is selected
    setUomFilterValue('');

    // If selecting a custom value (not in predefined list), save it for persistence
    if (value && value.trim() && !UOM_OPTIONS.includes(value.trim())) {
      setCustomUOMs([value.trim()]);
    }
    // Note: We don't clear customUOMs when selecting predefined options
    // The custom UOM persists until a new custom value is selected

    formik.setFieldValue('uom', value, false);
    formik.setFieldTouched('uom', true, false);
  };

  const handleUOMFilterChange = (event: any) => {
    const filterValue = event.filter?.value || '';
    // Update the filter state - this controls what's shown in dropdown
    setUomFilterValue(filterValue);
  };

  const handleUOMBlur = (event: any) => {
    // Call formik's handleBlur first
    formik.handleBlur(event);
    // Clear filter on blur
    setUomFilterValue('');
  };

  const selectedUOM = useMemo(() => {
    const found = uomOptions.find((opt) => opt.value === formik.values.uom);
    if (found) return found;
    if (formik.values.uom) {
      return { text: formik.values.uom, value: formik.values.uom };
    }
    return { text: '', value: '' };
  }, [uomOptions, formik.values.uom]);

  const getSaveButtonDisabledReason = (isSaving: boolean): string | null => {
    if (isSaving) {
      return 'Saving changes...';
    }
    const validationMessages = Object.values(formik.errors).filter((error) => error);
    if (validationMessages.length > 0) {
      return null;
    }
    const hasFormChanges = JSON.stringify(formik.values) !== JSON.stringify(initialValues);
    if (!hasFormChanges) {
      return 'No data changes';
    }
    return null;
  };

  return {
    formik,
    uomOptions,
    selectedUOM,
    uomFilterValue,
    handleUOMChange,
    handleUOMFilterChange,
    handleUOMBlur,
    getSaveButtonDisabledReason,
  };
};

export default useEditItemInformationForm;
